import _ from 'lodash';
import { EventSubscriptionsApi, FeedTankSensorTypeEnum, FeedTypeLevelMetricEnum, SubscriptionItem } from '@/api';
import { capitalize } from '@amcharts/amcharts4/.internal/core/utils/Utils';
import Toast from '@/components/display/toast.vue';
import { AxiosError } from 'axios';
import moment from 'moment';
import { Constants } from '@/common/Constants';

export function filterEntityEventSubscriptions(
	eventSubscriptions: EventSubscription[],
	entityId: number | undefined,
	entityType: string,
): EventSubscription[] {
	if (entityType == 'tanks') {
		return eventSubscriptions.filter(es => es.entityId == entityId);
	} else if (entityType === 'vats') {
		return eventSubscriptions;
	} else {
		return eventSubscriptions.filter(
			es => es.entityId == entityId && es.entityType!.includes(entityType),
		);
	}
}

export function filterEntityEventTypes(
	eventTypes: EventType[],
	entityType?: string | undefined,
): EventType[] {
	if (entityType && entityType !== 'tanks' && entityType !== 'vats') {
		return eventTypes.filter(et => et.entityType != null && et.entityType.includes(entityType));
	} else {
		return eventTypes;
	}
}

export function findSubscribableEventTypes(
	eventTypes: EventType[],
	eventSubscriptions: EventSubscription[],
	directTier?: string,
	subscriptionitem?: SubscriptionItem,
): EventType[] {
	let unsubscribableEvents = eventSubscriptions.map(es => {
		if (es.parameters === null || es.deletedAt !== null) {
			return es.eventTypeId;
		}
		return -1;
	});

	return eventTypes
		.filter(et => !unsubscribableEvents.includes(et.id) && et.deletedAt === null)
		.map(et => {
			return {
				...et,
				allowed:
					!directTier ||
					et.tierList.includes(directTier) ||
					(!!subscriptionitem?.trialPlan &&
						moment(subscriptionitem.trialEndsAt, 'YYYY-MM-DD hh:mm:ss').isAfter(moment().utc()) &&
						et.tierList.includes(subscriptionitem.trialPlan)),
			};
		});
}

export function createEventSubscriptionGroups(
	eventSubscriptions: EventSubscription[],
	eventTypes: EventType[],
	users: SubscribableUser[],
	unitType?: string,
	sensorType?: string,
): EventSubscriptionGroup[] {
	let es = eventSubscriptions.map(eventSubscription => {
		let eventType = eventTypes.find(et => et.id == eventSubscription.eventTypeId);
		let user = users.find(u => u.id == eventSubscription.userId);
		return {
			...eventSubscription,
			name: eventType!.name,
			eventType: eventType,
			title: eventType!.title,
			description: eventType!.description,
			user: user!,
		};
	});
	let eventSubscriptionGroups = _.groupBy(
		es,
		item => `${item.name}-${JSON.stringify(item.parameters)}`,
	);

	return Object.keys(eventSubscriptionGroups).map(esgk => {
		let {
			['name']: name,
			['title']: title,
			['description']: description,
			['parameters']: parameters,
			['productId']: productId,
			['entityId']: entityId,
			['entityType']: entityType,
			['eventType']: eventType,
			...rest
		} = eventSubscriptionGroups[esgk][0];
		return {
			name,
			title,
			description,
			parameters: eventType ? getEventParameters(eventType!, parameters, unitType, sensorType) : [],
			productId,
			entityId,
			entityType,
			eventType,
			key: esgk,
			parameterContext: eventParameterContext(name, parameters, unitType),
			subscriptions: eventSubscriptionGroups[esgk],
		};
	});
}

export async function createEventSubscription(eventSubscription: EventSubscription) {
	try {
		const response = await new EventSubscriptionsApi().postEventSubscription({
			data: eventSubscription,
		});
	} catch (e) {
		console.log(e);
		let error = 'Subscription not created';
		const axiosError = e as AxiosError;
		if (axiosError.response) {
			error = axiosError.response.data.errors;
		}
		new Toast().makeToast(error, 'Alert Error', 'danger', false);
	}
}

export async function deleteEventSubscription(eventSubscriptionId: number) {
	try {
		const response = await new EventSubscriptionsApi().deleteEventSubscription(eventSubscriptionId);
	} catch (e) {
		console.log(e);
		let error = 'Subscription not deleted';
		if (e.response) {
			error = e.response.data.errors;
		}
		new Toast().makeToast(error, 'Alert Error', 'danger', false);
	}
}

export async function patchEventSubscription(
	eventSubscriptionId: number,
	eventSubscription: EventSubscription,
) {
	try {
		await new EventSubscriptionsApi().patchEventSubscription(eventSubscriptionId, {
			data: eventSubscription,
		});
	} catch (e) {
		let error = 'Subscription not updated';
		if (e.response) {
			error = e.response.data.errors;
		}
		new Toast().makeToast(error, 'Alert Error', 'danger', false);
	}
}

function eventParameterContext(name: string, parameters?: any, unitType?: string): string {
	let eventParameterContext = '';
	if (parameters) {
		switch (name) {
			case 'lowSupplyFlow':
			case 'lowUsageFlow':
				eventParameterContext = `Under ${parameters.flow} L/min`;
				break;
			case 'highSupplyFlow':
			case 'highUsageFlow':
				eventParameterContext = `Over ${parameters.flow} L/min`;
				break;
			case 'lowLevel': {
				const levelValue =
					unitType === FeedTypeLevelMetricEnum.Weight && parameters.weight != null
						? parameters.weight + 'kg'
						: parameters.volume
						? parameters.volume + 'L'
						: parameters.percent
						? parameters.percent + '%'
						: '';
				const delayPart = parameters.delayUntil != null ? `, Delay Until ${parameters.delayUntil}` : '';
				eventParameterContext = `Under ${levelValue}${delayPart}`;
				break;
			}
			case 'highLevel':
				eventParameterContext = `Over ${
					unitType === FeedTypeLevelMetricEnum.Weight && parameters.weight != null
						? parameters.weight + 'kg'
						: parameters.volume
						? parameters.volume + 'L'
						: parameters.percent
						? parameters.percent + '%'
						: ''
				}`;
				break;
			case 'usageSupplyByPeriod':
			case 'usageUsageByPeriod':
				eventParameterContext = `${parameters.aboveBelow} ${parameters.volume}M\u00B3 over ${parameters.periodValue} ${parameters.periodUnits}s`;
				break;
			case 'overnightSupplyUse':
			case 'overnightUsageUse':
				eventParameterContext = `Exceeds ${parameters.volume} M\u00B3`;
				break;
			case 'usageSupplyByFixedPeriod':
			case 'usageUsageByFixedPeriod':
				eventParameterContext = `${
					parameters.volume
				}M\u00B3 between ${getDayWithOrdinalMonthAsString(
					parameters.startDay,
					parameters.startMonth,
				)} to ${getDayWithOrdinalMonthAsString(parameters.endDay, parameters.endMonth)} `;

				break;
			case 'withdrawalHours':
			case 'levelChangeHours':
				eventParameterContext = `Between ${parameters.begin_hours
					.toString()
					.padStart(2, '0')}:${parameters.begin_mins
					.toString()
					.padStart(2, '0')} - ${parameters.end_hours
					.toString()
					.padStart(2, '0')}:${parameters.end_mins.toString().padStart(2, '0')}`;

				break;
			case 'withdrawalVolume':
				eventParameterContext = `${capitalize(parameters.condition)} than ${parameters.absolute}L`;
				break;
			default:
				break;
		}
	}
	return eventParameterContext;
}

export function getEventParameters(
	eventType: EventType,
	existingParameters?: any,
	unitType?: string,
	sensorType?: string,
): EventParameter[] {
	const name = eventType.name;
	let eventParameters = { [name]: {} };
	switch (name) {
		case 'lowSupplyFlow':
		case 'lowUsageFlow':
		case 'highSupplyFlow':
		case 'highUsageFlow':
			eventParameters = {
				[name]: {
					flow: {
						type: 'number',
						unit: 'L/min',
						value: 0,
						name: '',
					},
				},
			};
			break;
		case 'overnightSupplyUse':
		case 'overnightUsageUse':
			eventParameters = {
				[name]: {
					volume: {
						type: 'number',
						unit: 'M\u00B3',
						value: 0,
					},
				},
			};
			break;
		case 'lowLevel': {
			const isStrainFeedTank =
				eventType.entityType === Constants.ENTITY_TYPE_FEED_TANK &&
				sensorType === FeedTankSensorTypeEnum.Strain;
			if (isStrainFeedTank) {
				eventParameters = {
					[name]: {
						unit: {
							type: 'hidden',
							value: 'weight',
						},
						value: {
							type: 'number',
							value: 1000,
							unit: 'kg',
							step: 500,
							min: 1000,
						},
						delayUntil: {
							type: 'time',
							value: null,
						},
					},
				};
			} else {
				const weight =
					eventType.entityType === Constants.ENTITY_TYPE_FEED_TANK &&
					unitType === FeedTypeLevelMetricEnum.Weight;
				eventParameters = {
					[name]: {
						value: {
							type: 'number',
							value: 0,
						},
						unit: {
							type: 'option',
							options: [
								{ text: '%', value: 'percent' },
								{
									text: eventType.productType === Constants.PRODUCT_TYPE_FUEL || !weight ? 'L' : 'kg',
									value: weight ? 'weight' : 'volume',
								},
							],
							value: null,
						},
						delayUntil: {
							type: 'hidden',
							value: null,
						},
					},
				};
			}
			break;
		}
		case 'highLevel': {
			const weight =
				eventType.entityType === Constants.ENTITY_TYPE_FEED_TANK &&
				unitType === FeedTypeLevelMetricEnum.Weight;
			eventParameters = {
				[name]: {
					value: {
						type: 'number',
						value: 0,
					},
					unit: {
						type: 'option',
						options: [
							{ text: '%', value: 'percent' },
							{
								text: eventType.productType === Constants.PRODUCT_TYPE_FUEL || !weight ? 'L' : 'kg',
								value: weight ? 'weight' : 'volume',
							},
						],
						value: null,
					},
				},
			};
			break;
		}
		case 'usageSupplyByPeriod':
		case 'usageUsageByPeriod':
			eventParameters = {
				[name]: {
					aboveBelow: {
						type: 'option',
						options: [
							{ text: 'Above', value: 'above' },
							{ text: 'Below', value: 'below' },
						],
						value: null,
					},
					volume: {
						type: 'number',
						value: 0,
						unit: 'M\u00B3',
					},
					periodValue: {
						type: 'number',
						value: 0,
					},
					periodUnits: {
						type: 'option',
						options: [
							{ text: 'Hours', value: 'hour' },
							{ text: 'Days', value: 'day' },
							{ text: 'Weeks', value: 'week' },
						],
						value: null,
					},
				},
			};
			break;
		case 'usageSupplyByFixedPeriod':
		case 'usageUsageByFixedPeriod':
			eventParameters = {
				[name]: {
					startDay: {
						type: 'daySelect',
						value: 1,
					},
					startMonth: {
						type: 'option',
						options: [
							{ text: 'January', value: 1 },
							{ text: 'February', value: 2 },
							{ text: 'March', value: 3 },
							{ text: 'April', value: 4 },
							{ text: 'May', value: 5 },
							{ text: 'June', value: 6 },
							{ text: 'July', value: 7 },
							{ text: 'August', value: 8 },
							{ text: 'September', value: 9 },
							{ text: 'October', value: 10 },
							{ text: 'November', value: 11 },
							{ text: 'December', value: 12 },
						],
						value: 1,
					},
					endDay: {
						type: 'daySelect',
						value: 1,
					},
					endMonth: {
						type: 'option',
						options: [
							{ text: 'January', value: 1 },
							{ text: 'February', value: 2 },
							{ text: 'March', value: 3 },
							{ text: 'April', value: 4 },
							{ text: 'May', value: 5 },
							{ text: 'June', value: 6 },
							{ text: 'July', value: 7 },
							{ text: 'August', value: 8 },
							{ text: 'September', value: 9 },
							{ text: 'October', value: 10 },
							{ text: 'November', value: 11 },
							{ text: 'December', value: 12 },
						],
						value: 12,
					},
					volume: {
						type: 'number',
						value: 0,
						unit: 'M\u00B3',
					},
				},
			};
			break;
		case 'withdrawalHours':
		case 'levelChangeHours':
			eventParameters = {
				[name]: {
					begin_value: {
						type: 'time',
						value: '19:00:00',
					},
					end_value: {
						type: 'time',
						value: '6:00:00',
					},
				},
			};
			break;
		case 'withdrawalVolume':
			eventParameters = {
				[name]: {
					condition: {
						type: 'option',
						options: [
							{ text: 'Greater Than', value: 'more' },
							{ text: 'Less Than', value: 'less' },
						],
						value: null,
					},
					absolute: {
						type: 'number',
						value: 0,
						unit: 'L',
					},
				},
			};
			break;
		default:
			eventParameters = { [name]: {} };
			break;
	}

	if (existingParameters) {
		switch (name) {
			case 'lowLevel':
				Object.keys(existingParameters!).forEach(p => {
					if (p === 'delayUntil') {
						const raw = existingParameters[p];
						const hh = String(Math.floor(raw / 100)).padStart(2, '0');
						const mm = String(raw % 100).padStart(2, '0');
						eventParameters[name].delayUntil.value = `${hh}:${mm}:00`;
					} else {
						eventParameters[name].unit.value = p;
						eventParameters[name].value.value = existingParameters[p];
					}
				});
				break;
			case 'highLevel':
				Object.keys(existingParameters!).forEach(p => {
					eventParameters[name].unit.value = p;
					eventParameters[name].value.value = existingParameters[p];
				});
				break;
			case 'withdrawalHours':
			case 'levelChangeHours':
				eventParameters[name].begin_value.value =
					existingParameters['begin_value'] != undefined
						? existingParameters['begin_value']
						: `${existingParameters['begin_hours']
								.toString()
								.padStart(2, '0')}:${existingParameters['begin_mins']
								.toString()
								.padStart(2, '0')}:00`;
				eventParameters[name].end_value.value =
					existingParameters['end_value'] != undefined
						? existingParameters['end_value']
						: `${existingParameters['end_hours'].toString().padStart(2, '0')}:${existingParameters[
								'end_mins'
						  ]
								.toString()
								.padStart(2, '0')}:00`;

				break;
			default:
				Object.keys(existingParameters!).forEach(p => {
					eventParameters[name][p].value = existingParameters[p];
				});

				break;
		}
	}
	// Object.keys(existingParameters).find(key => key === 'begin_value')
	return eventParameters;
}

function getDayWithOrdinalMonthAsString(day: number, month: number) {
	const s = ['th', 'st', 'nd', 'rd'],
		v = day % 100;
	const m = [
		'January',
		'February',
		'March',
		'April',
		'May',
		'June',
		'July',
		'August',
		'September',
		'October',
		'November',
		'December',
	];
	return day + (s[(v - 20) % 10] || s[v] || s[0]) + ` of ${m[month - 1]}`;
}

export interface EventType {
	id: number;
	name: string;
	title: string;
	defaultParameters: null | object;
	description: string;
	entityType?: string;
	productType: string;
	isDefault: boolean;
	isExperimental: boolean;
	isSpecial: boolean;
	productId: number;
	tierList: string[];
	allowed?: boolean;
}

export interface SubscribableUser {
	id: number;
	name: string;
	phone: string;
	email: string;
}

export const parameterTimePeriod = [
	{ value: 'day', text: 'Daily' },
	{ value: 'month', text: 'Monthly' },
	{ value: 'quarter', text: 'Quarterly' },
];

export interface EventSubscriptionGroup {
	key?: string;
	name: string | undefined;
	parameterContext?: null | string | undefined;
	parameters?: null | {} | undefined;
	subscriptions: EventSubscription[];
	eventTypeId?: number | undefined;
	eventType: EventType | undefined;
	entityId?: number;
	entityType?: string;
	productId: number;
}

export interface EventParameter {
	type: string;
	unit?: string;
	value: string | number | null;
	name: string;
	options?: { text: string; value: string | number }[];
	step?: number;
	min?: number;
}
export interface EventSubscription {
	id?: number;
	eventTypeId: number;
	eventType?: EventType | undefined;
	userId: number;
	user?: SubscribableUser;
	parameters?: null | object | undefined;
	productId: number;
	entityId?: number;
	entityType?: string;
	channel: string;
}
